import * as React from "react";
import styled, {css} from "styled-components";
import {withAppStores} from "../../../../AppStores";
import type {AppState} from "../../../../data/state/AppState";
import type {IFieldPointer} from "../../../../data/models/field/Field";
import type {ManageColumnCallback} from "../../../widgets/table/TableHeaderDropDown";
import type {IViewColumn, IViewSort} from "../../../../data/models/ViewUtils";
import {getSortForField, getSortIndex} from "../../../../data/models/ViewUtils";
import {XyiconFeature} from "../../../../generated/api/base";
import type {View} from "../../../../data/models/View";
import type {Pointer} from "../../../../utils/interaction/Pointer";
import {
	checkboxWidth,
	minHeaderWidth,
	noTableRowIconLeftPadding,
	stickyReportTableRowIconWidth,
	stickyTableRowIconWidth,
	tableCellOffset,
} from "../../../widgets/table/TableConstants";
import type {ColumnDropIndicatorSide} from "../../../widgets/table/TableUtils";
import {getColumnDropIndicatorSide, getColumnDropIndicatorSideFromPosition, getColumnDropReorderIndex} from "../../../widgets/table/TableUtils";
import {ReactUtils} from "../../../utils/ReactUtils";
import {CheckboxInputV5} from "../../details/datatypes/CheckboxInputV5";
import {colorPalette} from "../../styles/colorPalette";
import {baseDistance, FLEXCENTER, fontSize} from "../../styles/styles";
import {TableHeaderStyled, TableHeaderV5} from "./TableHeaderV5";

interface ITableHeadersProps {
	readonly headers: Partial<IViewColumn>[];
	readonly checkboxColumn: boolean;
	readonly allSelected: boolean;
	readonly headerRef: React.RefObject<HTMLTableSectionElement>;
	readonly tableRowIcon?: boolean;
	readonly feature: XyiconFeature;
	readonly view: View;
	readonly sorts: IViewSort[];
	readonly appState?: AppState;

	readonly onAllClicked: () => void;
	readonly onToggleSort: (column: IFieldPointer) => void;
	readonly onAutoResizeColumn: (index: number, header: Partial<IViewColumn>) => void;
	readonly onReorderColumn?: (index: number, newIndex: number) => void;
	readonly onHeaderWidthChange: (index: number, width: number, header: Partial<IViewColumn>) => void;
	readonly onHeaderWidthPreview?: (index: number, width: number, header: Partial<IViewColumn>) => void;
	readonly onManageColumns?: ManageColumnCallback;
	readonly onSortAsc?: (id: IFieldPointer) => void;
	readonly onSortDesc?: (id: IFieldPointer) => void;
	readonly onClearSort?: (id: IFieldPointer) => void;
}

interface ITableHeadersState {
	dragSourceIndex: number;
	dragTarget: number;
	dragTargetSide: ColumnDropIndicatorSide | null;
}

@withAppStores("appState")
export class TableHeadersV5 extends React.Component<ITableHeadersProps, ITableHeadersState> {
	private _columnDrags: number[] = [];
	private _columnDragWidths: number[] = [];
	private _columnDragMoved: boolean[] = [];

	constructor(props: ITableHeadersProps) {
		super(props);

		this.state = {
			dragSourceIndex: -1,
			dragTarget: -1,
			dragTargetSide: null,
		};
	}

	private onDragStart = (index: number, event: React.DragEvent) => {
		if (this.props.onReorderColumn) {
			this.setState({dragSourceIndex: index});
		}
	};

	private onDragEnd = (index: number, event: React.DragEvent) => {
		if (this.props.onReorderColumn) {
			const {dragSourceIndex, dragTarget, dragTargetSide} = this.state;

			this.setState({
				dragSourceIndex: -1,
				dragTarget: -1,
				dragTargetSide: null,
			});

			const newIndex = getColumnDropReorderIndex(dragSourceIndex, dragTarget, dragTargetSide);

			if (newIndex === null) {
				return;
			}

			this.props.onReorderColumn(dragSourceIndex, newIndex);
		}
	};

	private onDragEnter = (index: number, event: React.DragEvent) => {
		if (this.props.onReorderColumn) {
			this.setState({dragTarget: index, dragTargetSide: this.getDragTargetSide(event)});
		}
	};

	private onDragOver = (index: number, event: React.DragEvent) => {
		if (this.props.onReorderColumn) {
			event.preventDefault();
			this.setState({dragTarget: index, dragTargetSide: this.getDragTargetSide(event)});
		}
	};

	private onDragLeave = (index: number, event: React.DragEvent) => {
		if (this.props.onReorderColumn) {
			this.setState({dragTarget: -1, dragTargetSide: null});
		}
	};

	private getDragTargetSide = (event: React.DragEvent): ColumnDropIndicatorSide => {
		const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect();

		return getColumnDropIndicatorSideFromPosition(event.clientX, bounds.left, bounds.width);
	};

	private onColResizeStart = (index: number, pointer: Pointer) => {
		this._columnDrags[index] = this.props.headers[index].width || minHeaderWidth;
		this._columnDragWidths[index] = this._columnDrags[index];
		this._columnDragMoved[index] = false;
	};

	private onColResizeMove = (index: number, pointer: Pointer, header: Partial<IViewColumn>) => {
		const width = Math.max(minHeaderWidth, this._columnDrags[index] + pointer.offsetX);

		this._columnDragWidths[index] = width;
		this._columnDragMoved[index] = true;

		if (this.props.onHeaderWidthPreview) {
			this.props.onHeaderWidthPreview(index, width, header);
		} else {
			this.props.onHeaderWidthChange(index, width, header);
		}
	};

	private onColResizeEnd = (index: number, pointer: Pointer, header: Partial<IViewColumn>) => {
		if (this.props.onHeaderWidthPreview && this._columnDragMoved[index]) {
			const width = this._columnDragWidths[index] ?? this._columnDrags[index] ?? minHeaderWidth;

			this.props.onHeaderWidthChange(index, width, header);
		}
	};

	public override render() {
		const {
			headers,
			checkboxColumn,
			allSelected,
			tableRowIcon,
			feature,
			appState,
			view,
			sorts,
			headerRef,
			onAllClicked,
			onSortAsc,
			onSortDesc,
			onClearSort,
			onToggleSort,
			onAutoResizeColumn,
			onManageColumns,
		} = this.props;
		const {dragSourceIndex, dragTarget, dragTargetSide} = this.state;
		const validDragTargetSide = getColumnDropIndicatorSide(dragSourceIndex, dragTarget, dragTargetSide);
		const multipleSorts = appState.actions.getSortsForFeature(view, feature).length > 1;

		return (
			<TableHeadersStyled
				ref={headerRef}
				className={ReactUtils.cls("head", {tableRowIcon, report: feature === XyiconFeature.Report})}
				$checkboxColumn={checkboxColumn}
			>
				<tr>
					{tableRowIcon && <TableHeadersRowIconColumnStyled className={ReactUtils.cls({report: feature === XyiconFeature.Report})} />}
					{checkboxColumn && (
						<TableHeadersCheckBoxColumnStyled
							onClick={onAllClicked}
							className={ReactUtils.cls({report: feature === XyiconFeature.Report})}
							style={{width: checkboxWidth, minWidth: checkboxWidth}}
						>
							<CheckboxInputV5
								value={allSelected}
								disabled={true}
							/>
							<div className="resizer checkbox" />
						</TableHeadersCheckBoxColumnStyled>
					)}
					{headers.map((header, index) => {
						const sortIndex = getSortIndex(view, header.field, appState, feature);
						const sort = getSortForField(sorts, header.field) || sorts[0];

						return (
							<TableHeaderV5
								key={header.field}
								header={header}
								index={index}
								sortIndex={sortIndex}
								sort={sort}
								multipleSorts={multipleSorts}
								dragSource={index === dragSourceIndex}
								dragTarget={index === dragTarget && validDragTargetSide !== null}
								dragTargetSide={validDragTargetSide}
								onDragStart={this.onDragStart}
								onDragEnd={this.onDragEnd}
								onDragEnter={this.onDragEnter}
								onDragOver={this.onDragOver}
								onDragLeave={this.onDragLeave}
								onColResizeStart={this.onColResizeStart}
								onColResizeMove={this.onColResizeMove}
								onColResizeEnd={this.onColResizeEnd}
								onToggleSort={onToggleSort}
								onAutoResizeColumn={onAutoResizeColumn}
								onManageColumns={onManageColumns}
								allowHide={headers.length > 1}
								feature={feature}
								onSortAsc={onSortAsc}
								onSortDesc={onSortDesc}
								onClearSort={onClearSort}
							/>
						);
					})}
					<TableHeaderFillerStyled />
				</tr>
			</TableHeadersStyled>
		);
	}
}

export const TableHeadersRowIconColumnStyled = styled.th`
	position: sticky;
	left: 0;
	z-index: 2;
	width: ${stickyTableRowIconWidth}px;
	min-width: ${stickyTableRowIconWidth}px;
	height: 39px;
	padding: 0;
	background-color: ${colorPalette.gray.c100};

	&.report {
		width: ${stickyReportTableRowIconWidth}px;
		min-width: ${stickyReportTableRowIconWidth}px;
	}
`;

export const TableHeadersCheckBoxColumnStyled = styled.th`
	border-right: 1px solid ${colorPalette.gray.c400};
	height: 100%;
	${FLEXCENTER};

	&.fixed {
		position: absolute;
		background: ${colorPalette.gray.c100};
		z-index: 9;
		height: inherit;
	}
`;

export const TableHeadersStyled = styled.thead<{$checkboxColumn?: boolean}>`
	display: flex;
	user-select: none;
	text-align: justify;
	background: ${colorPalette.gray.c100};
	height: 40px;
	align-items: center;
	color: ${colorPalette.gray.c700Dark};
	font-size: ${fontSize.md};
	line-height: 16px;
	border-bottom: 1px solid ${colorPalette.gray.c500Primary};
	position: sticky;
	top: 0;
	z-index: 3;

	&:not(.tableRowIcon) {
		padding-left: ${noTableRowIconLeftPadding}px;
	}

	tr {
		display: flex;
		align-items: center;
		height: inherit;
		width: 100%;
	}

	.CheckboxInput {
		position: relative;
		right: 2px;
	}

	${(props) =>
		props.$checkboxColumn &&
		css`
			th${TableHeadersCheckBoxColumnStyled} {
				position: sticky;
				width: ${checkboxWidth}px;
				left: 0;
				padding-left: ${baseDistance.xs};
				background-color: ${colorPalette.gray.c100};
				z-index: 2;
				height: 39px;
				padding-right: ${baseDistance.sm};
				padding-left: 7px;
			}
		`};

	&.tableRowIcon {
		th${TableHeadersCheckBoxColumnStyled} {
			left: ${stickyTableRowIconWidth}px;
			/* The cell starts tableCellOffset earlier, keep the checkbox aligned with the ones in the rows */
			padding-left: 10px;
			padding-right: 5px;
		}

		&.report th${TableHeadersCheckBoxColumnStyled} {
			left: ${stickyReportTableRowIconWidth}px;
		}

		/* The field cells are shifted right in the rows, keep the columns aligned with them */
		${TableHeadersRowIconColumnStyled} + ${TableHeaderStyled},
		${TableHeadersCheckBoxColumnStyled} + ${TableHeaderStyled} {
			margin-left: ${tableCellOffset}px;
		}
	}
`;

export const TableHeaderFillerStyled = styled.th`
	flex: 1;
	min-width: 0;
	pointer-events: none;
	cursor: default;
	padding: 0;
`;
